import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FileCheck, FileX, Clock, CheckCircle2, XCircle, Loader2, ArrowLeft, Eye, Download } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDocumentVerificationPage() {
  const navigate = useNavigate();
  const { isAuthenticated, user, token, loading } = useAuth();
  const [verifications, setVerifications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [filter, setFilter] = useState("all");
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewType, setPreviewType] = useState("image");
  const [previewLoading, setPreviewLoading] = useState(false);

  // 서류 미리보기: 인증 헤더가 필요한 엔드포인트이므로 fetch로 받아 blob URL 생성
  // (<img src>는 Authorization 헤더를 보낼 수 없어 직접 src 지정 시 401로 깨짐)
  const openPreview = async (doc) => {
    setPreviewLoading(true);
    setPreviewUrl(null);
    try {
      const res = await fetch(`${API_URL}${doc.file_url}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        if (res.status === 404) {
          alert("파일이 삭제되었거나 존재하지 않습니다. (승인 완료된 서류는 보안상 원본이 삭제됩니다)");
        } else {
          alert(`서류를 불러오지 못했습니다. (오류 ${res.status})`);
        }
        return;
      }
      const blob = await res.blob();
      setPreviewType(blob.type === "application/pdf" || doc.file_url.endsWith(".pdf") ? "pdf" : "image");
      setPreviewUrl(URL.createObjectURL(blob));
    } catch (e) {
      alert("서류를 불러오는 중 오류가 발생했습니다.");
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
  };

  useEffect(() => {
    if (loading) return;
    if (!isAuthenticated || user?.role !== "admin") { navigate("/"); return; }
    fetchVerifications();
  }, [isAuthenticated, user, loading]);

  const fetchVerifications = async () => {
    setIsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/document-verifications`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setVerifications(data.verifications || []);
      }
    } catch (err) { console.error(err); }
    finally { setIsLoading(false); }
  };

  const handleApprove = async (docId) => {
    setActionLoading(docId);
    try {
      const res = await fetch(`${API_URL}/api/admin/document-verifications/${docId}/approve`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) fetchVerifications();
    } catch (err) { console.error(err); }
    finally { setActionLoading(null); }
  };

  const handleReject = async () => {
    if (!rejectModal) return;
    setActionLoading(rejectModal);
    try {
      const res = await fetch(`${API_URL}/api/admin/document-verifications/${rejectModal}/reject`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ reason: rejectReason }),
      });
      if (res.ok) { setRejectModal(null); setRejectReason(""); fetchVerifications(); }
    } catch (err) { console.error(err); }
    finally { setActionLoading(null); }
  };

  const filtered = filter === "all" ? verifications : verifications.filter(v => v.status === filter);
  const counts = {
    all: verifications.length,
    pending: verifications.filter(v => v.status === "pending").length,
    approved: verifications.filter(v => v.status === "approved").length,
    rejected: verifications.filter(v => v.status === "rejected").length,
  };

  const statusConfig = {
    pending: { label: "심사 대기", icon: Clock, color: "text-amber-600 bg-amber-50 border-amber-200" },
    approved: { label: "승인 완료", icon: CheckCircle2, color: "text-green-600 bg-green-50 border-green-200" },
    rejected: { label: "반려", icon: XCircle, color: "text-red-600 bg-red-50 border-red-200" },
  };

  const docTypeLabels = { marriage_certificate: "혼인관계증명서" };

  if (loading || isLoading) {
    return (
      <div className="min-h-screen bg-stone-100 pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-stone-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-100 pt-20" data-testid="admin-doc-verify-page">
      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate("/admin")} className="p-2 hover:bg-stone-200 rounded-lg transition-colors" data-testid="back-to-admin">
            <ArrowLeft className="w-5 h-5 text-stone-500" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-stone-800">서류인증 관리</h1>
            <p className="text-sm text-stone-400">회원 서류를 확인하고 승인/반려합니다</p>
          </div>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-2 mb-6">
          {[
            { key: "all", label: "전체" },
            { key: "pending", label: "심사 대기" },
            { key: "approved", label: "승인" },
            { key: "rejected", label: "반려" },
          ].map(({ key, label }) => (
            <button key={key} onClick={() => setFilter(key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${filter === key ? "bg-stone-800 text-white" : "bg-white text-stone-500 hover:bg-stone-50 border border-stone-200"}`}
              data-testid={`filter-${key}`}>
              {label} <span className="ml-1 text-xs opacity-70">({counts[key]})</span>
            </button>
          ))}
        </div>

        {/* List */}
        {filtered.length === 0 ? (
          <div className="bg-white rounded-xl border border-stone-200 p-12 text-center">
            <FileCheck className="w-12 h-12 text-stone-300 mx-auto mb-3" />
            <p className="text-stone-400 text-sm">해당하는 서류가 없습니다.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((doc) => {
              const st = statusConfig[doc.status] || statusConfig.pending;
              const StIcon = st.icon;
              return (
                <div key={doc.id} className="bg-white rounded-xl border border-stone-200 p-5" data-testid={`doc-item-${doc.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-semibold text-stone-800">{doc.username}</span>
                        <span className="text-stone-400 text-sm">{doc.email}</span>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${st.color}`}>
                          <StIcon className="w-3 h-3" /> {st.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-stone-400">
                        <span>서류: {docTypeLabels[doc.doc_type] || doc.doc_type}</span>
                        <span>제출: {new Date(doc.submitted_at).toLocaleString("ko-KR")}</span>
                        {doc.reviewed_at && <span>심사: {new Date(doc.reviewed_at).toLocaleString("ko-KR")}</span>}
                      </div>
                      {doc.review_note && (
                        <p className="mt-2 text-xs text-red-500 bg-red-50 px-3 py-1.5 rounded-lg inline-block">반려 사유: {doc.review_note}</p>
                      )}
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      {/* Preview button */}
                      <button onClick={() => openPreview(doc)}
                        className="p-2 text-stone-400 hover:text-stone-600 hover:bg-stone-100 rounded-lg transition-colors disabled:opacity-50" title="미리보기"
                        disabled={previewLoading}
                        data-testid={`preview-${doc.id}`}>
                        {previewLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                      </button>

                      {doc.status === "pending" && (
                        <>
                          <Button onClick={() => handleApprove(doc.id)} disabled={actionLoading === doc.id}
                            className="bg-green-600 hover:bg-green-700 text-white text-xs px-3 h-8 gap-1"
                            data-testid={`approve-${doc.id}`}>
                            {actionLoading === doc.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><CheckCircle2 className="w-3 h-3" /> 승인 (+500P)</>}
                          </Button>
                          <Button onClick={() => { setRejectModal(doc.id); setRejectReason(""); }}
                            variant="outline" className="text-red-500 border-red-200 hover:bg-red-50 text-xs px-3 h-8 gap-1"
                            data-testid={`reject-${doc.id}`}>
                            <XCircle className="w-3 h-3" /> 반려
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Image preview modal */}
      {previewUrl && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={closePreview} data-testid="preview-modal">
          <div className="max-w-3xl max-h-[85vh] bg-white rounded-xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-3 border-b border-stone-200">
              <span className="text-sm font-medium text-stone-600">서류 미리보기</span>
              <button onClick={closePreview} className="p-1 hover:bg-stone-100 rounded"><XCircle className="w-5 h-5 text-stone-400" /></button>
            </div>
            <div className="p-4 overflow-auto max-h-[75vh]">
              {previewType === "pdf" ? (
                <iframe src={previewUrl} className="w-full h-[70vh]" title="Document preview" />
              ) : (
                <img src={previewUrl} alt="Document" className="max-w-full rounded-lg" />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reject reason modal */}
      {rejectModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setRejectModal(null)} data-testid="reject-modal">
          <div className="bg-white rounded-xl w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-5">
              <h3 className="text-lg font-bold text-stone-800 mb-1">서류 반려</h3>
              <p className="text-sm text-stone-400 mb-4">반려 사유를 입력해주세요.</p>
              <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                className="w-full px-3 py-2 border border-stone-200 rounded-lg bg-stone-50 focus:outline-none focus:ring-2 focus:ring-stone-400/20 resize-none min-h-[80px] text-sm"
                placeholder="예: 서류가 불선명합니다. 다시 제출해주세요." data-testid="reject-reason-input" />
              <div className="flex gap-2 mt-4">
                <Button onClick={() => setRejectModal(null)} variant="outline" className="flex-1">취소</Button>
                <Button onClick={handleReject} disabled={actionLoading} className="flex-1 bg-red-600 hover:bg-red-700 text-white" data-testid="reject-confirm-btn">
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "반려하기"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
